import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    MenuItem,
    TextField,
    Typography
} from "@mui/material"
import React, { useEffect, useState } from "react"
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  addWorkerAccount,
  checkIfWorkerWithPTSExists,
  getAllCompetenecies,
  getAllExperiences,
  GetAllWorkRole,
  getTickets,
} from "../../../api/apiCall";
import { useAuth } from "../../../auth/auth";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type FormValues = {
    name: string;
    surname: string;
    pts: string;
    phoneNumber: string;
    email: string;
    postCode: string;
    location: string;
    suffix: string;
    password: string;
    notes: string;
    driver: boolean;
    competencies: any[];
    experiences: string[];
    tickets: string[];
    tier: number;
    workRoles: string[];
}

const CreateWorker = () => {
    const {state} = useAuth();
    const [competencies, setComptenecies] = useState<any>([]);
    const [experiences, setExperiences] = useState<any>([]);
    const [tickets, setTickets] = useState<any>([]);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [selectedCompetencies, setSelectedCompetencies] = useState<string[]>([]);
    const [selectedExperiences, setSelecetedExperiences] = useState<string[]>([]);
    const [selectedTickets, setSelectedTickets] = useState<string[]>([]);
    const [workRoles, setWorkRoles] = useState<any>([]);
    const [selectedWorkRoles, setSelectedWorkRoles] = useState<string[]>([]);
    const {
      register,
      handleSubmit,
      formState: { errors },
      setValue,
      reset,
      setError,
      clearErrors,
    } = useForm<FormValues>();

    const createWorker = async (data: any) => {
        data.tier = Number(data.tier);
        const ptsExists = await checkIfWorkerWithPTSExists(state.user?.token, data.pts);
        if(ptsExists) {
            return;
        }

        setButtonLoading(true);
        data.competencies = selectedCompetencies;
        data.experiences = selectedExperiences;
        data.tickets = selectedTickets;
        data.workRoles = selectedWorkRoles;
        data.password = "PaceWorker";

        const response = await addWorkerAccount(state.user?.token, data);
        if(response) {
            setTimeout(() => {
                setButtonLoading(false);
                toast.success('Worker added successfully');
                reset({
                    name: '',
                    surname: '',
                    pts: 'N/A',
                    phoneNumber: '',
                    email: 'N/A',
                    postCode: 'N/A',
                    location: 'N/A',
                    suffix: 'N/A',
                    password: 'PaceWorker',
                    notes: '',
                    driver: false,
                    competencies: [],
                    experiences: [],
                    tickets: [],
                    workRoles: [],
                    tier: -1
                });
            }, 500);
        }
    };

    const workerWithPTSExists = async (ptsNumber: any) => {
        const response = await checkIfWorkerWithPTSExists(state.user?.token, ptsNumber);
        if(response) {
            setError("pts", {
              message: "This PTS number is already assigned to another worker",
            });
        } else {
            clearErrors("pts");
        }
    };

    useEffect(() => {
        getAllCompetenecies(state.user?.token).then(setComptenecies);
        getAllExperiences(state.user?.token).then(setExperiences);
        getTickets(state.user?.token).then(setTickets);
        GetAllWorkRole(state.user?.token).then(setWorkRoles);
    }, []);

    return (
        <Box bgcolor={"#f2f5fc"} height={'93vh'}>
            <Box
                sx={{
                    maxWidth: 1000,
                    margin: "auto",
                    padding: 5,
                    backgroundColor: "#fff"
                }}
            >
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Create New Worker
                </Typography>
                <Box sx={{borderBottom: 1, borderColor: "divider", mb: 2}} />
                <Box display="grid" gridTemplateColumns={{xs: "1fr", md: "1fr 1fr"}} gap={3} mt={7}>
                    <TextField
                        label="Name"
                        placeholder="Name"
                        variant="outlined"
                        size="small"
                        fullWidth
                        {...register("name", {
                            required: "Name is required",
                            minLength: {
                                value: 3,
                                message: "Name must be at least 3 characters"
                            },
                            maxLength: {
                                value: 20,
                                message: "Name must be at most 20 characters"
                            }
                        })}
                        onBlur={(e) => {
                            const trimmedValue = e.target.value.trim();
                            setValue("name", trimmedValue);
                        }}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        {...register("competencies")}
                        options={competencies}
                        disableCloseOnSelect
                        onChange={(_, newValue) => {
                            const competenciesSelected = newValue.map(item => item.id);
                            setSelectedCompetencies(competenciesSelected)
                        }}
                        getOptionLabel={(option: any) => option.skillName}
                        renderOption={(props, option, { selected }) => {
                            const { key, ...optionProps }: any = props;
                            return (
                                <li key={key} {...optionProps}>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                        {option.skillName}
                                </li>
                            );
                        }}
                        style={{ width: 500 }}
                        renderInput={(params) => (
                            <TextField {...params} label="Competencies" placeholder="Select Competencies" />
                        )}
                    />
                    <TextField
                        label="Surname"
                        size="small"
                        variant="outlined"
                        fullWidth
                        error={!!errors.surname}
                        {...register("surname", {
                            required: "Surname is required",
                            minLength: {
                                value: 3,
                                message: "Surname must be at least 3 characters"
                            },
                            maxLength: {
                                value: 20,
                                message: "Surname must be at most 20 characters"
                            }
                        })}
                        helperText={errors.surname?.message}
                        onBlur={(e) => {
                            const trimmedValue = e.target.value.trim();
                            setValue("surname", trimmedValue);
                        }}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        options={experiences}
                        disableCloseOnSelect
                        {...register("experiences")}
                        onChange={(_, newValue) => {
                            const experiencesSelected = newValue.map(item => item.id);
                            setSelecetedExperiences(experiencesSelected)
                        }}
                        getOptionLabel={(option: any) => option.experienceName}
                        renderOption={(props, option, { selected }) => {
                            const { key, ...optionProps }: any = props;
                            return (
                                <li key={key} {...optionProps}>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                        {option.experienceName}
                                </li>
                            );
                        }}
                        style={{width: 500}}
                        renderInput={(params) => (
                            <TextField {...params} label="Experiences" placeholder="Select Experiences" />
                        )}
                    />
                    <TextField
                        label="PTS"
                        size="small"
                        variant="outlined"
                        fullWidth
                        defaultValue={"N/A"}
                        error={!!errors.pts}
                        helperText={errors.pts?.message}
                        {...register("pts")}
                        onBlur={(e) => {
                            const trimmedValue = e.target.value.trim();
                            setValue("pts", trimmedValue);
                            workerWithPTSExists(trimmedValue)
                        }}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        {...register("tickets")}
                        options={tickets}
                        disableCloseOnSelect
                        onChange={(_, newValue) => {
                            const ticketsSelected = newValue.map(item => item.id);
                            setSelectedTickets(ticketsSelected)
                        }}
                        getOptionLabel={(option: any) => option.ticketName}
                        renderOption={(props, option, { selected }) => {
                            const { key, ...optionProps }: any = props;
                            return (
                                <li key={key} {...optionProps}>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                        {option.ticketName}
                                </li>
                            );
                        }}
                        style={{width: 500}}
                        renderInput={(params) => (
                            <TextField {...params} label="Tickets" placeholder="Select Tickets" />
                        )}
                    />
                    <TextField
                        label="Phone Number"
                        variant="outlined"
                        fullWidth
                        size="small"
                        error={!!errors.phoneNumber}
                        {...register("phoneNumber", {
                            required: "Phone Number is required",
                            maxLength: {
                                value: 20,
                                message: "Phone Number must be at most 20 characters"
                            }
                        })}
                        helperText={errors.phoneNumber?.message}
                        onBlur={(e) => {
                            const trimmedValue = e.target.value.trim();
                            setValue("phoneNumber", trimmedValue);
                        }}
                    />
                    <TextField
                        select
                        fullWidth
                        placeholder="Select Tier"
                        size="small"
                        label="Tier"
                        defaultValue={-1}
                        {...register("tier")}
                    >
                        <MenuItem value={-1}>No Tier</MenuItem>
                        <MenuItem value={0}>Active - Currently resourced on live shift</MenuItem>
                        <MenuItem value={1}>Tier 1 - Not Working / Available {"<"} 24 hours</MenuItem>
                        <MenuItem value={2}>Tier 2 - Working Available {"<"} 7 days</MenuItem>
                        <MenuItem value={3}>Tier 3 - Not looking for work currently</MenuItem>
                        <MenuItem value={4}>Tier 4 - Weekend Worker</MenuItem>
                        <MenuItem value={5}>Tier 5 - Wrong or missing contact details</MenuItem>
                    </TextField>
                    <TextField
                        label="Email"
                        variant="outlined"
                        fullWidth
                        size="small"
                        defaultValue={"N/A"}
                        {...register("email")}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        options={workRoles}
                        disableCloseOnSelect
                        {...register("workRoles")}
                        onChange={(_, newValue) => {
                            const workRolesSelected = newValue.map(item => item.id);
                            setSelectedWorkRoles(workRolesSelected);
                        }}
                        getOptionLabel={(option: any) => option.workRoleName}
                        renderOption={(props, option, { selected }) => {
                            const { key, ...optionProps }: any = props;
                            return (
                                <li key={key} {...optionProps}>
                                    <Checkbox
                                      icon={icon}
                                      checkedIcon={checkedIcon}
                                      style={{ marginRight: 8 }}
                                      checked={selected}
                                    />
                                    {option.workRoleName}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Work Roles" />
                        )}
                        sx={{
                            '& .MuiAutocomplete-inputRoot': {
                                flexWrap: 'nowrap',
                                overflowX: 'auto',
                                scrollbarWidth: 'thin',
                                '&::-webkit-scrollbar': {
                                  height: '9px',
                                },
                            },
                            '& .MuiChip-root': {
                                marginRight: '4px',
                                maxWidth: '150px',
                                overflow: 'hidden',
                            },
                        }}
                    />
                    <TextField
                        label="Location"
                        variant="outlined"
                        fullWidth
                        size="small"
                        {...register("location")}
                        defaultValue={"N/A"}
                    />
                    <TextField
                        label="Post Code"
                        variant="outlined"
                        fullWidth
                        size="small"
                        defaultValue={"N/A"}
                        {...register("postCode")}
                    />
                    <TextField
                        label="Password"
                        variant="outlined"
                        fullWidth
                        size="small"
                        defaultValue={"PaceWorker"}
                        error={!!errors.password}
                        disabled
                        helperText={errors.password?.message}
                        {...register("password", {
                            minLength: {
                                value: 6,
                                message: "Password must be at least 6 characters"
                            },
                            maxLength: {
                                value: 20,
                                message: "Password must be at most 20 characters"
                            }
                        })}
                    />
                </Box>
                <TextField
                    sx={{mt: 3}}
                    label="Notes"
                    variant="outlined"
                    fullWidth
                    size="small"
                    multiline
                    rows={4}
                    {...register("notes")}
                />
                <Typography style={{fontSize: 17}}>Driver:<Checkbox {...register("driver")} /></Typography>
                <Box display="flex" justifyContent="center" mt={3}>
                    <Button
                        variant="contained"
                        color="primary"
                        style={{boxShadow: "none", backgroundColor: "#156bc2", width: 160, color: "white"}}
                        onClick={handleSubmit(createWorker)}
                        disabled={!!errors.pts || buttonLoading}
                    >
                        {buttonLoading ? (
                            <CircularProgress size={25} style={{color: 'white'}} />
                        ) : ('Create Worker')}
                    </Button>
                </Box>
            </Box>
        </Box>
    )
};

export default CreateWorker;